import { query } from "@solidjs/router";

import { getServerEnv } from "~/_server/server";
import { getUserByEmail, updateDB } from "~/_server";
import { getXeroClient } from "~/_server/xero";
import {
	Invoice,
	Invoice as InvoiceType,
	Contact as ContactType,
	Payment,
	OnlineInvoice,
} from "xero-node";

type InvoiceDTO = {
	invoiceId?: string;
	invoiceNumber?: string;
	date?: string | null;
	dueDate?: string | null;
	status?: Invoice["status"];
	total?: number | null;
	amountDue?: number | null;
	amountPaid?: number | null;
	contact?: { contactID?: string; name?: string; emailAddress?: string };
};

const toDTO = (i: InvoiceType): InvoiceDTO => ({
	invoiceId: i.invoiceID,
	invoiceNumber: i.invoiceNumber,
	date: i.date ? new Date(i.date).toISOString() : null,
	dueDate: i.dueDate ? new Date(i.dueDate).toISOString() : null,
	status: i.status,
	total: i.total ?? null,
	amountDue: i.amountDue ?? null,
	amountPaid: i.amountPaid ?? null,
	contact: i.contact
		? {
				contactID: i.contact.contactID,
				name: i.contact.name,
				emailAddress: i.contact.emailAddress,
			}
		: undefined,
});

const toMs = (d: unknown): number =>
	d ? new Date(d as any).getTime() : -Infinity;

// xero-node rejects with a plain object rather than an Error, so `.message` is
// undefined and the real reason (e.g. a duplicate invoice number) was lost.
const describeXeroError = (e: any): string => {
	const detail = e?.response?.body ?? e?.body ?? e?.message ?? e;

	try {
		return typeof detail === "string" ? detail : JSON.stringify(detail);
	} catch {
		return String(detail);
	}
};

const reverseDate = (d: Date): string =>
	`${d.getFullYear().toString().slice(2)}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;

async function withXeroRetry<T>(
	apiCall: () => Promise<T>,
	maxAttempts = 5,
): Promise<T> {
	let attempts = 0;

	while (attempts < maxAttempts) {
		try {
			return await apiCall();
		} catch (e: any) {
			attempts++;

			const isRateLimit = e.response?.statusCode === 429;
			const isConcurrent =
				e.response?.headers?.["x-rate-limit-problem"] === "concurrent";

			if ((isRateLimit || isConcurrent) && attempts < maxAttempts) {
				// Use Xero's retry-after header if it exists, otherwise exponential backoff
				const retryHeader = parseInt(e.response?.headers?.["retry-after"]);
				const waitTimeSeconds = retryHeader || Math.pow(2, attempts);

				// Convert to ms and add a bit of random jitter (0-200ms)
				const delay = waitTimeSeconds * 1000 + Math.random() * 200;

				console.warn(
					`Xero Rate Limit (${isConcurrent ? "Concurrent" : "Standard"}). Retrying in ${Math.round(delay)}ms... (Attempt ${attempts}/${maxAttempts})`,
				);

				await new Promise(resolve => setTimeout(resolve, delay));
			} else {
				// Log and re-throw the error so the calling function knows it failed
				console.error("Xero API Final Failure:", describeXeroError(e));
				throw e;
			}
		}
	}
	throw new Error("Max retry attempts reached");
}

export const createInvoice = async (
	userEmail: string,
	userName: string,
	lineItems: InvoiceType["lineItems"],
	itemId: number,
	date: string = "",
	// Distinguishes follow-on certificates (stages/amendments) that share the
	// parent's itemId - Xero invoice numbers must be unique.
	suffix: string = "",
): Promise<{ invoiceId: string | undefined; invoiceNumber: string | undefined}> => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const contact = await getContactByEmail(userEmail, true);

		if (contact?.contactID) {
			let dateObj;
			if (date == "") {
				dateObj = new Date();
			} else {
				dateObj = new Date(date);
			}

			const invoiceNumber =  `RR-${reverseDate(dateObj)}-${String(itemId).padStart(5, "0")}${suffix}`;

			const invoice = {
				invoiceNumber,
				type: Invoice.TypeEnum.ACCREC,
				contact: { contactID: contact?.contactID ?? null, name: userName },
				lineItems,
				date: dateObj.toISOString(),
				dueDate: dateObj.toISOString(),
				status: Invoice.StatusEnum.AUTHORISED,
			};

			const invoiceId = await withXeroRetry(async () => {
				const result = await xeroClient.accountingApi.createInvoices(tenantId, {
					invoices: [invoice],
				});
				return result.body?.invoices?.[0]?.invoiceID ?? undefined;
			} );
			

			return {invoiceNumber, invoiceId};
		}
	} catch (e: unknown) {
		console.log("Error creating invoice:", describeXeroError(e));
	}

	return {invoiceNumber:undefined, invoiceId:undefined};
};

export const setInvoicePending = async (userId: number, invoiceId: string) => {
	"use server";

	try {
		await updateDB(`USER#${userId}`, `INVOICE#${invoiceId}`, {
			status: "pending",
			_ttl: 60 * 60 * 24 * 30,
		}); // pending for 30 days

		return { success: true };
	} catch (e: unknown) {
		console.error("Error setting invoice pending:", (e as Error).message);

		return { success: false, error: (e as Error).message };
	}
};

export const markInvoiceAsPaid = async (invoiceID: string, amount: number) => {
	"use server";
	console.log( "Marking invoice as paid", invoiceID, amount );
	try {
		const { xeroClient, tenantId } = await getXeroClient();
		const accountID = getServerEnv().xeroAccountId;

		const payment = {
			invoice: { invoiceID },
			account: { accountID },
			date: new Date().toISOString().split("T")[0],
			amount,
		} as Payment;

		const result = await xeroClient.accountingApi.createPayment(
			tenantId,
			payment,
		);

		return JSON.parse( JSON.stringify( result.body.payments?.[0] ) ); 
	} catch (error) {
		console.error("Error markInvoiceAsPaid:", error);
	}

	return null;
};

export const createContact = async (userEmail: string) => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const user = await getUserByEmail(userEmail);

		if (user) {
			const contact: ContactType = {
				name: user.name,
				emailAddress: user.email,
				firstName: user.firstName,
				lastName: user.lastName,
			};

			return await withXeroRetry(async () => {
				const result = await xeroClient.accountingApi.createContacts(tenantId, {
					contacts: [contact],
				});

				return result.body.contacts?.[0];
			});
		}
	} catch (error) {
		console.error("Error creating contact:", error);
	}

	return null;
};

export const getContactByEmail = async (
	email: string,
	createIfNotExists = false,
) => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		let contact = await withXeroRetry(async () => {
			const contactRes = await xeroClient.accountingApi.getContacts(
				tenantId,
				undefined,
				`EmailAddress=="${email}"`,
			);

			return contactRes?.body?.contacts?.[0];
		});

		if (!contact?.contactID && createIfNotExists) {
			contact = await withXeroRetry(async () => await createContact(email));
		}

		if (contact?.contactID) return contact;
	} catch (error) {
		console.error("Error fetching contact by email:", error);
	}
};

export const getInvoiceUrl = async (
	invoiceId: string,
): Promise<OnlineInvoice | { error: string }> => {
	"use server";

	try {
		const { xeroClient, tenantId } = await getXeroClient();

		return await withXeroRetry(async () => {
			const invoice = await xeroClient.accountingApi.getOnlineInvoice(
				tenantId,
				invoiceId,
			);

			if (
				invoice.body.onlineInvoices &&
				invoice.body.onlineInvoices.length > 0
			) {
				return invoice.body.onlineInvoices[0];
			} else {
				return { error: "Invoice not found" };
			}
		});
	} catch (e: unknown) {
		console.error(e);
		return { error: (e as Error).message };
	}
};

export const getCustomerInvoices = query(async (email: string) => {
	"use server";
	try {
		const { xeroClient, tenantId } = await getXeroClient();

		const contact = await getContactByEmail(email, false);

		if (contact) {
			// Step 2: Get invoices for this contact
			const invoiceRes = await xeroClient.accountingApi.getInvoices(
				tenantId,
				undefined,
				`Contact.ContactID==Guid("${contact.contactID}")`,
				undefined, // order
				undefined, // IDs
				undefined, // invoiceNumbers
				undefined, // contact IDs
				["AUTHORISED", "PAID"], // statuses
				undefined, // page
				undefined, // includeArchived
				false, // whereOnlyInvoices
			);

			const invoices = (invoiceRes.body.invoices ?? [])
				.slice()
				.filter(inv => inv.status != Invoice.StatusEnum.DELETED)
				.sort((a, b) => toMs(b.date) - toMs(a.date)) // date DESC, nulls last
				.map(toDTO);

			return invoices;
		} else {
			return [];
		}
	} catch (e: unknown) {
		console.error(e);
		return [{ error: (e as Error).message }];
	}
}, "getCustomerInvoices");
